import {
  ActionIcon,
  Button,
  Checkbox,
  Chip,
  Flex,
  Icon,
  SegmentedControl,
  Select,
  Stack,
  TextSearch,
  desktopFirstMediaQuery,
  desktopFirstQuery,
} from '@inflearn/ds-react';
import { KoOnly, useLang } from '@inflearn/i18n-tools';
import { faSearch } from '@inflearn/pro-regular-svg-icons';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import { isEqual, merge, pick } from 'lodash-es';
import { useTranslation } from 'next-i18next';
import { useEffect, useMemo, useState } from 'react';

import { CourseType, MyVoucherStudyingType } from '../../../../../resources/@types/courseSchema';
import useQueryHandleWithUserId from '../../../../hooks/urlQueryString/useQueryHandleWithUserId';
import type { StringifyObject } from '../../../../utils/typeUtil';
import { forLegacyParameter } from './forLegacyParameter';
import type { VoucherMyQueryParameter } from './queries/useClientApiVoucherMyQuery';
import { CoursesSort } from './sort';

export function MyCoursesPageFilter({
  filterState,
  changeFilter,
}: {
  filterState: StringifyObject<VoucherMyQueryParameter>;
  changeFilter: (value: StringifyObject<VoucherMyQueryParameter>) => void;
}) {
  const { USER_ID_KEY } = useQueryHandleWithUserId();

  const { isKo } = useLang();
  const { t } = useTranslation();

  useEffect(() => {
    const changedFilter = forLegacyParameter(filterState);

    if (isEqual(changedFilter, filterState)) {
      // ant-man-account에서 온게 아님
      return;
    }
    // ant-man-account에서 온 경우
    changeFilter(changedFilter);
  }, []);

  const isOfflineCourseType = filterState.courseType === CourseType.OFF;
  const isOnlineCourseType = filterState.courseType === CourseType.ON;
  const isChallengeCourseType = filterState.courseType === CourseType.CHALLENGE;
  const isMobile = useMediaQuery(desktopFirstQuery.mobile);

  const gapSize = isMobile ? 'sm' : 'md';

  const updateFilter = (key: keyof typeof filterState) => (value: string) => {
    const filterObject = merge(filterState, { pageNumber: '1', [key]: value });

    if (key === 'courseType') {
      const isOfflineType = value === CourseType.OFF;
      changeFilter(
        merge(
          pick(filterObject, [
            'courseType',
            'pageNumber',
            'isArchived',
            'searchKeyword',
            USER_ID_KEY,
          ]),
          {
            sort: isOfflineType ? CoursesSort.RECENT_APPLY : CoursesSort.RECENT_STUDYING,
          },
        ),
      );

      return;
    }

    changeFilter(filterObject);
  };

  return (
    <Stack spacing="md" mt={16}>
      <Flex
        justify="space-between"
        gap={gapSize}
        wrap="wrap"
        css={{
          ...(!isKo && {
            order: 2,
            justifyContent: 'flex-end',
          }),
        }}
      >
        <KoOnly>
          <CourseTypeFilter
            value={filterState.courseType}
            updateFilter={updateFilter('courseType')}
          />
        </KoOnly>
        {!isKo && isMobile ? null : (
          <KeywordFilter
            isMobile={isMobile}
            value={filterState.searchKeyword}
            updateFilter={updateFilter('searchKeyword')}
          />
        )}
      </Flex>
      <Flex justify="space-between" gap={gapSize} wrap="wrap">
        {isOnlineCourseType && (
          <Flex gap={4} align="center">
            <StudyFilter updateFilter={updateFilter('studying')} value={filterState.studying} />
            <ReviewFilter
              updateFilter={updateFilter('isReviewable')}
              value={filterState.isReviewable}
            />
          </Flex>
        )}
        {!isKo && isMobile ? (
          <KeywordFilter
            isMobile={isMobile}
            value={filterState.searchKeyword}
            updateFilter={updateFilter('searchKeyword')}
          />
        ) : null}
        <Flex
          gap={gapSize}
          align="center"
          justify={isOfflineCourseType ? 'flex-end' : 'space-between'}
          css={{
            flexGrow: 1,
            [desktopFirstMediaQuery.mobile]: {
              width: '100%',
            },
          }}
        >
          {isOnlineCourseType && (
            <Checkbox
              onChange={(e) =>
                updateFilter('isExcludeExpired')(e.target.checked ? 'true' : 'false')
              }
              defaultChecked={isTrueParameter(filterState.isExcludeExpired)}
              label={t('만료 제외')}
              size="sm"
              radius="sm"
            />
          )}
          {!isChallengeCourseType && (
            <SortOption
              value={filterState.sort}
              updateFilter={updateFilter('sort')}
              isOfflineFilter={isOfflineCourseType}
            />
          )}
        </Flex>
      </Flex>
    </Stack>
  );
}

function isTrueParameter(value: string | undefined) {
  return value === 'true';
}

/**
 * 수강평 작성 가능 여부 필터
 */
function ReviewFilter({
  updateFilter,
  value,
}: {
  value: string | undefined;
  updateFilter: (value: string) => void;
}) {
  const { t } = useTranslation();
  const checked = isTrueParameter(value);

  return (
    <Chip
      checked={checked}
      onChange={(v) => updateFilter(v ? 'true' : '')}
      variant="outline"
      color="gray"
      size="xs"
      radius="xl"
    >
      <span>{t('수강평 작성 가능')}</span>
    </Chip>
  );
}

/**
 * 온/오프라인 필터
 */
function CourseTypeFilter({
  value,
  updateFilter,
}: {
  value?: string;
  updateFilter: (value: string) => void;
}) {
  const { t } = useTranslation();

  const options = CourseTypeFilter.option.map((option) => ({
    value: option.value,
    label: t(option.label),
  }));

  return <SegmentedControl value={value} onChange={updateFilter} data={options} />;
}

export const COURSE_TYPE = {
  ON: '온라인',
  CHALLENGE: '챌린지',
  OFF: '모임/부트캠프',
} as const;

CourseTypeFilter.option = Object.entries(COURSE_TYPE).map(([value, label]) => ({
  value,
  label: label,
}));

/**
 * 키워드 검색 (지공자, 강의명 등)
 */
function KeywordFilter({
  isMobile,
  value = '',
  updateFilter,
}: {
  isMobile: boolean;
  value?: string;
  updateFilter: (value: string) => void;
}) {
  const { t } = useTranslation();
  const [keyword, setKeyword] = useState(value);
  const [isKeywordOpen, keywordOpenHandler] = useDisclosure();

  useEffect(() => {
    setKeyword(value);
  }, [value]);

  const isKeywordShow = useMemo(() => {
    if (!isMobile) {
      return true;
    }

    return isMobile && isKeywordOpen;
  }, [isMobile, isKeywordOpen]);

  const toggleKeywordOpen = () => {
    if (isKeywordOpen) {
      keywordOpenHandler.close();

      return;
    }
    keywordOpenHandler.open();
  };

  const handleEnter = () => {
    updateFilter(keyword);
  };

  const handleClickSearchIcon = () => {
    updateFilter(keyword);
  };

  const handleClear = () => {
    setKeyword('');
    updateFilter('');
  };

  return (
    <Flex align="center" w={isMobile && isKeywordShow ? '100%' : undefined} gap={8}>
      {isKeywordShow && (
        <TextSearch
          size="sm"
          width={isMobile ? '100%' : 250}
          css={{ flexGrow: isMobile ? 1 : undefined }}
        >
          <TextSearch.TextInput
            onKeyDown={(e) => e.key === 'Enter' && handleEnter()}
            onChange={(e) => setKeyword(e.target.value)}
            onClear={handleClear}
            placeholder={t('강의명, 지식공유자 이름 검색')}
            value={keyword}
          />

          <TextSearch.ActionIcon onClick={handleClickSearchIcon} />
        </TextSearch>
      )}
      {isMobile && !isKeywordOpen && (
        <ActionIcon
          variant="default"
          onClick={toggleKeywordOpen}
          size={isMobile ? 'n-xs' : 'n-sm'}
          aria-label={t('키워드 검색열기')}
        >
          <Icon icon={faSearch} size="sm" />
        </ActionIcon>
      )}

      {isMobile && isKeywordOpen && (
        <Button
          onClick={() => {
            toggleKeywordOpen();
            handleClear();
          }}
          variant="subtle"
          color="dark"
          size="xs"
          compact
          radius="md"
        >
          {t('취소')}
        </Button>
      )}
    </Flex>
  );
}

/**
 * 완강, 학습중 필터
 */
function StudyFilter({
  value,
  updateFilter,
}: {
  value?: string;
  updateFilter: (value: string) => void;
}) {
  const { t } = useTranslation();

  return (
    <>
      {[MyVoucherStudyingType.STUDYING, MyVoucherStudyingType.COMPLETED].map((studyEnum) => (
        <Chip
          key={studyEnum}
          onClick={() => updateFilter(studyEnum === value ? '' : studyEnum)}
          checked={StudyFilter.option[studyEnum].value === value}
          variant="outline"
          color="gray"
          size="xs"
          radius="xl"
        >
          <span>{t(StudyFilter.option[studyEnum].label)}</span>
        </Chip>
      ))}
    </>
  );
}

StudyFilter.option = {
  [MyVoucherStudyingType.STUDYING]: {
    value: MyVoucherStudyingType.STUDYING,
    label: '학습중',
  },
  [MyVoucherStudyingType.COMPLETED]: {
    value: MyVoucherStudyingType.COMPLETED,
    label: '완강.short',
  },
} as const;

export const MY_COURSES_SORTING = {
  RECENT_STUDYING: '최근 학습순',
  RECENT_APPLY: '최근 신청순',
  COURSE_TITLE: '제목순',
  EARLIEST_EXPIRATION: '만료일순',
} as const;

/**
 * 정렬
 * @constructor
 */
function SortOption({
  value,
  updateFilter,
  isOfflineFilter,
}: {
  value?: string;
  updateFilter: (value: string) => void;
  isOfflineFilter: boolean;
}) {
  const { t } = useTranslation();

  const options = useMemo(
    () =>
      (isOfflineFilter
        ? SortOption.option.filter(
            (item) =>
              item.value !== CoursesSort.RECENT_STUDYING &&
              item.value !== CoursesSort.EARLIEST_EXPIRATION,
          )
        : SortOption.option
      ).map((option) => ({ value: option.value, label: t(option.label) })),
    [isOfflineFilter],
  );

  return <Select w={180} size="sm" value={value} data={options} onChange={updateFilter} />;
}

SortOption.option = Object.entries(MY_COURSES_SORTING).map(([value, label]) => ({
  value,
  label,
}));
